/**
 * The task — Spec 022 (round-2 mockups, agreed by both agents).
 *
 * A header — the status, the title, «Сейчас… Дальше…», who has it — and a row of
 * block buttons with counts; one block is open. On entry the block that needs
 * you is open (a result waiting for your check), otherwise the discussion. The
 * discussion is messages and quiet system lines only; the journal is its own
 * block with its own paging. Status, assignee, moving, agent access and delete
 * live under «···».
 */

import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal, RotateCcw } from "lucide-react";

import { api, ApiError } from "@/api/client";
import { Me, Projects, Tasks } from "@/api/resources";
import type { StatusCategory } from "@/api/types";
import { liveTaskQueryOptions } from "@/boot/query-sync";
import { AttachmentsSection } from "@/components/AttachmentsSection";
import { ChatThread } from "@/components/ChatThread";
import { ConflictSheet } from "@/components/ConflictSheet";
import { SourceIdeas } from "@/components/IdeaWork";
import { InlineText } from "@/components/InlineText";
import { DescriptionChanged } from "@/components/TaskHistory";
import { TaskAssignment } from "@/components/TaskAssignment";
import { TaskJournal } from "@/components/TaskJournal";
import { TaskHandover, TaskLeadState } from "@/components/TaskLead";
import { TaskApplications, TaskSuggestions } from "@/components/TaskKnowledge";
import { TaskAgentAccess } from "@/screens/ConnectionsScreen";
import { BlockedNotice, TaskLinks } from "@/components/TaskLinks";
import { TaskCode } from "@/components/TaskCode";
import { TaskMemory } from "@/components/TaskMemory";
import { TaskMove } from "@/components/TaskMove";
import { CloseTaskSheet, ExtractTaskMethod } from "@/components/TaskReportSheet";
import { TaskResultReview } from "@/components/TaskResultReview";
import { TaskSyncStatus } from "@/components/TaskSyncStatus";
import { ToolChip } from "@/components/ToolChip";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { TextArea, TextField } from "@/components/ui/Field";
import { Sheet } from "@/components/ui/Sheet";
import { statusName } from "@/lib/statusName";
import { Spinner } from "@/components/ui/Spinner";
import { Mark, Status, type StatusTone } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";
import { AppBar, blockPanel, BlockTabs, Caption, EmptyNote, genitive, NowLine, useMedia, type BlockTab } from "@/kit";
import { cn } from "@/lib/cn";
import { nameOf, usePeople } from "@/lib/people";
import { lastActionOf } from "@/screens/live";
import { Markdown } from "@/lib/markdown";
import { useBackButton } from "@/lib/telegram-ui";
import { useVersionedUpdate } from "@/lib/useVersionedUpdate";

type Block = "discussion" | "result" | "description" | "journal" | "links" | "knowledge" | "files" | "code";
const BLOCKS: Block[] = ["discussion", "result", "description", "journal", "links", "knowledge", "files", "code"];
type ResultState = "pending" | "confirmed" | "changes_requested" | "skipped";

export function TaskScreenRoute() {
  const { task_id } = useParams<{ task_id: string }>();
  useBackButton("/");
  return task_id ? <TaskScreen taskId={task_id} /> : null;
}

/** `embedded`: shown in the right pane on a computer — no bar of its own. */
export function TaskScreen({ taskId, embedded = false }: { taskId: string; embedded?: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const me = useQuery({ queryKey: ["me"], queryFn: Me.get });
  const task = useQuery({ queryKey: ["task", taskId], queryFn: () => Tasks.get(taskId), ...liveTaskQueryOptions });
  const statuses = useQuery({
    queryKey: ["project-statuses", task.data?.project_id],
    queryFn: () => Projects.statuses(task.data!.project_id),
    enabled: Boolean(task.data?.project_id),
  });
  const project = useQuery({
    queryKey: ["project", task.data?.project_id],
    queryFn: () => Projects.get(task.data!.project_id),
    enabled: Boolean(task.data?.project_id),
  });
  // «Сейчас по задаче» stays live while the task is open for work.
  const live = task.data != null && task.data.completed_at == null;
  const journal = useQuery({ queryKey: ["task-journal", taskId], queryFn: () => Tasks.listJournal(taskId), refetchInterval: live ? 15_000 : false });
  const people = usePeople();
  const result = useQuery({
    queryKey: ["task-result", taskId],
    queryFn: () => api<{ state: ResultState; final?: boolean } | null>(`/api/v1/tasks/${taskId}/result`),
    ...liveTaskQueryOptions,
  });

  type TaskPatch = Omit<Parameters<typeof Tasks.update>[1], "version">;
  const update = useVersionedUpdate<TaskPatch, Awaited<ReturnType<typeof Tasks.update>>>({
    version: task.data?.version,
    mutate: (body) => Tasks.update(taskId, body),
    invalidate: [["task", taskId], ["tasks"], ["project-tasks"], ["projects"], ["project"], ["suggestions", taskId], ["task-history", taskId], ["chat"], ["overview"]],
    onError: (error) => toast.error(error.message),
  });
  const reopen = useMutation({
    mutationFn: () => Tasks.reopen(taskId),
    onSuccess: () => {
      for (const key of [["task", taskId], ["project-tasks"], ["tasks"], ["projects"], ["overview"]]) qc.invalidateQueries({ queryKey: key });
      toast.success("Задача снова в работе");
    },
    onError: () => toast.error("Не удалось вернуть задачу в работу"),
  });

  const [chosen, setChosen] = useState<Block | null>(null);
  const [editing, setEditing] = useState(false);
  const [managing, setManaging] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const navigate = useNavigate();
  // Two taps, as with messages: window.confirm never shows in Telegram's WebView.
  const remove = useMutation({
    mutationFn: () => Tasks.remove(taskId),
    onSuccess: () => {
      for (const key of [["project-tasks"], ["tasks"], ["projects"], ["overview"]]) qc.invalidateQueries({ queryKey: key });
      toast.success("Задача удалена");
      setManaging(false);
      if (embedded) {
        const next = new URLSearchParams(params);
        next.delete("open");
        setParams(next, { replace: true });
      } else if (window.history.length > 1) navigate(-1);
      else navigate("/");
    },
    onError: (error: Error) => toast.error(error.message || "Не удалось удалить задачу"),
  });
  const [closingStatus, setClosingStatus] = useState<string | null>(null);
  const [draft, setDraft] = useState({ title: "", summary: "" });
  // PC audit 28.09, point 17: a task page on a computer uses the width — the
  // work on the left, where it stands on the right — and a long title folds.
  const split = useMedia("(min-width: 1280px)") && !embedded;
  const [wholeTitle, setWholeTitle] = useState(false);

  if (task.isLoading) return <Spinner />;
  if (task.error instanceof ApiError && task.error.status === 404) return <Card><p>Задача не найдена.</p></Card>;
  if (task.isError || !task.data) return <TaskSyncStatus query={task} />;

  const t = task.data;
  const lastAction = lastActionOf(t.last_action);
  const category: StatusCategory | undefined = statuses.data?.find((s) => s.id === t.status_id)?.category;
  const isDone = t.completed_at != null;
  const canReview = Boolean(me.data && (!t.assignee_id || t.assignee_id === me.data.id));
  // Task f8e48cc9: an intermediate draft waits for nobody — the task is still in work.
  const resultState: ResultState | undefined = result.data?.state === "pending"
    && result.data.final === false ? undefined : result.data?.state;
  const needsMe = resultState === "pending" && canReview;
  // Sent back with «поправить»: still mine to confirm as it is (PC audit, point 2).
  const sentBack = resultState === "changes_requested" && canReview;
  const entries = journal.data ?? [];
  const latest = [...entries].reverse().find((e) => e.current_stage || e.next_step || e.blocker || e.needs);
  const overdue = t.due_at && new Date(t.due_at) < new Date() && !isDone;

  // Opens on the block that needs you (Kostya's agent's rule), unless chosen.
  const fromUrl = params.get("block");
  const block: Block = chosen ?? (fromUrl && (BLOCKS as string[]).includes(fromUrl) ? (fromUrl as Block) : needsMe ? "result" : "discussion");
  const choose = (key: Block) => {
    setChosen(key);
    const next = new URLSearchParams(params);
    next.set("block", key);
    setParams(next, { replace: true });
  };

  // Spec 020 D3: a status only from the set; closed without a check is not «Сделано».
  const tone: StatusTone | null = isDone
    ? resultState === "skipped" || resultState === "pending" ? null : "done"
    : needsMe ? "needs"
    : resultState === "pending" ? "review"
    : latest?.blocker ? "blocked"
    : (t.blocked_by?.length ?? 0) > 0 ? "waiting"
    : null;
  const mark = isDone && (resultState === "skipped" || resultState === "pending") ? "закрыта без проверки"
    : category === "cancelled" ? "отменена"
    : !tone && category === "in_progress" ? "в работе"
    : !tone && category === "backlog" ? (t.assignee_id ? "в очереди" : "можно взять")
    : null;

  const now = needsMe ? "результат ждёт твоей проверки."
    : resultState === "pending" ? `результат ждёт проверки ${t.assignee_name ? genitive(t.assignee_name) : "исполнителя"}.`
    : latest?.current_stage ?? null;
  const next = needsMe || resultState === "pending" ? null : latest?.next_step ?? null;

  const tabs: BlockTab<Block>[] = [
    { key: "discussion", label: "Обсуждение" },
    { key: "result", label: "Результат", alert: needsMe || sentBack, hidden: !result.data },
    { key: "description", label: "Описание", hidden: !t.description_md },
    { key: "journal", label: "Журнал", count: entries.length },
    { key: "links", label: "Связи", count: t.blocked_by?.length || undefined },
    { key: "knowledge", label: "Знания" },
    { key: "files", label: "Файлы" },
    { key: "code", label: "Код" },
  ];

  const openEdit = () => {
    setDraft({ title: t.title, summary: t.summary ?? "" });
    setEditing(true);
  };
  const saveEdit = async () => {
    const patch: TaskPatch = {};
    if (draft.title.trim() && draft.title.trim() !== t.title) patch.title = draft.title.trim();
    if (draft.summary.trim() !== (t.summary ?? "")) patch.summary = draft.summary.trim() || null;
    if (!Object.keys(patch).length) return setEditing(false);
    if (await update.run(patch)) setEditing(false);
  };

  const longTitle = t.title.length > 110;
  const nowAndMeta = (
    <>
      <NowLine
        now={now}
        next={next}
        blocker={latest?.blocker}
        needs={latest?.needs ? `от ${genitive(nameOf(people.data, latest.needs_user_id) ?? "участника")}: ${latest.needs}` : null}
        last={lastAction}
        onOpen={() => choose("journal")}
      />
      <p className="flex flex-wrap gap-x-3 text-meta text-p-text-muted">
        <span>{t.assignee_id ? <>Исполнитель <b className="font-bold text-p-text">{t.assignee_name ?? "участник"}</b></> : "Свободна — можно взять"}</span>
        {t.due_at && <span>до {new Date(t.due_at).toLocaleDateString("ru-RU", { day: "numeric", month: "short" })}</span>}
        {isDone && (
          <button type="button" className="inline-flex items-center gap-1 font-semibold text-p-link" onClick={() => reopen.mutate()}>
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />Вернуть в работу
          </button>
        )}
      </p>
      {/* Spec 034: the lead pair, its agent's state and what the task waits for. */}
      {!isDone && <TaskLeadState lead={t.lead} />}
    </>
  );
  const body = (
    <>
      <BlockTabs label="Блоки задачи" id="task-blocks" tabs={tabs} value={block} onChange={choose} />

      <div {...blockPanel("task-blocks", block)} className="flex flex-col gap-3 pt-1">
        {block === "discussion" && (
          <ChatThread key={t.id} context={{ parent_type: "task", parent_id: t.id }} hideSearch stickyComposer placeholder="Написать в задачу" />
        )}
        {block === "result" && (
          result.data ? <TaskResultReview taskId={t.id} version={t.version} canReview={canReview} />
            : <EmptyNote>Результата пока нет. Агент сохранит его, когда закончит, — тогда здесь можно будет проверить и подтвердить.</EmptyNote>
        )}
        {block === "description" && (
          <Card className="space-y-3" padding={16}>
            {t.summary && <p className="text-cell font-semibold text-p-text">{t.summary}</p>}
            <Markdown className="text-body" text={t.description_md} />
            <DescriptionChanged taskId={t.id} />
            <div><Button size="compact" variant="secondary" onClick={openEdit}>Изменить название и суть</Button></div>
          </Card>
        )}
        {block === "journal" && <TaskJournal taskId={t.id} />}
        {block === "code" && <TaskCode taskId={t.id} projectId={t.project_id} me={me.data?.id} canWrite={!isDone && me.data?.id === t.assignee_id && !project.data?.archived_at} />}
        {block === "links" && (
          <>
            <BlockedNotice blockers={t.blocked_by ?? []} />
            <TaskLinks taskId={t.id} />
          </>
        )}
        {block === "knowledge" && (
          <>
            <SourceIdeas kind="task" id={t.id} />
            <TaskSuggestions taskId={t.id} version={t.version} />
            <TaskMemory taskId={t.id} query={[t.summary, t.title].filter(Boolean).join(" ")} />
            <TaskApplications taskId={t.id} />
            <ExtractTaskMethod taskId={t.id} />
            {isDone && (
              <Card className="space-y-2">
                <Caption>Итог (предыдущее закрытие)</Caption>
                <InlineText value={t.completion_note_md} ariaLabel="Описание решения" placeholder="Опиши, как решена задача"
                  multiline onCommit={(v) => update.run({ completion_note_md: v })} saving={update.pending} />
              </Card>
            )}
          </>
        )}
        {block === "files" && <AttachmentsSection parentType="task" parentId={t.id} />}
      </div>
    </>
  );

  return (
    <div className={embedded ? "flex min-h-full flex-col gap-3" : "flex flex-col gap-3"}>
      <TaskSyncStatus query={task} />
      <AppBar
        fallback="/"
        back={!embedded}
        context={project.data?.title}
        trailing={
          <button type="button" aria-label="Статус, исполнитель и другое" onClick={() => setManaging(true)}
            className="flex h-11 w-11 items-center justify-center rounded-full text-p-text press-surface">
            <MoreHorizontal className="h-6 w-6" aria-hidden="true" />
          </button>
        }
      />

      {(tone || mark || overdue) && (
        <div className="flex flex-wrap items-center gap-2">
          {tone && <Status tone={tone} />}
          {mark && <Mark>{mark}</Mark>}
          {overdue && <span className="rounded-full border border-p-border-strong px-2 text-chip font-semibold text-p-text">просрочено</span>}
        </div>
      )}
      <h1 className={cn("text-[21px] font-bold leading-[27px] text-p-text lg:text-[26px] lg:leading-8", longTitle && !wholeTitle && "line-clamp-3")}>{t.title}</h1>
      {longTitle && (
        <button type="button" className="-mt-2 self-start text-meta font-semibold text-p-link" onClick={() => setWholeTitle((v) => !v)}>
          {wholeTitle ? "Свернуть название" : "Название полностью"}
        </button>
      )}
      {!split && nowAndMeta}
      {split ? (
        <div className="grid grid-cols-[minmax(0,1fr)_300px] items-start gap-10">
          <div className="flex min-w-0 flex-col gap-3">{body}</div>
          <aside className="sticky top-8 flex flex-col gap-3">{nowAndMeta}</aside>
        </div>
      ) : body}

      <Sheet open={editing} onClose={() => setEditing(false)} title="Изменить задачу"
        footer={<Button block variant="strong" loading={update.pending} loadingText="Сохраняем…"
          disabled={!draft.title.trim()} onClick={() => void saveEdit()}>Сохранить</Button>}>
        <div className="space-y-3">
          <TextField label="Название" value={draft.title} maxLength={300} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <TextArea label="Коротко: о чём задача" value={draft.summary} maxLength={240} rows={3}
            onChange={(e) => setDraft({ ...draft, summary: e.target.value })} />
        </div>
      </Sheet>

      <Sheet open={managing} onClose={() => { setManaging(false); setConfirmDelete(false); }} title="Задача">
        <div className="space-y-4 pb-2">
          <section className="space-y-2">
            <Caption>Статус</Caption>
            <div className="grid grid-cols-2 gap-2">
              {(statuses.data ?? []).filter((s) => s.id !== t.status_id).map((s) => (
                <Button key={s.id} variant="secondary" size="compact" loading={update.pending}
                  onClick={() => {
                    setManaging(false);
                    if (s.category === "done") setClosingStatus(s.id);
                    else void update.run({ status_id: s.id });
                  }}>
                  → {statusName(s.name)}
                </Button>
              ))}
            </div>
          </section>
          <TaskAssignment key={t.id} task={t} myUserId={me.data?.id}
            closed={isDone || category === "done" || category === "cancelled"} />
          <TaskHandover task={t} closed={isDone || category === "done" || category === "cancelled"} onDone={() => setManaging(false)} />
          {/* Right after who does it: who of the agents may work on it (PC audit, point 3). */}
          <TaskAgentAccess taskId={t.id} canGrant={Boolean(me.data && t.assignee_id === me.data.id)} />
          <Button variant="secondary" block onClick={() => { setManaging(false); openEdit(); }}>Изменить название и суть</Button>
          <TaskMove task={t} />
          {t.tools_used.length > 0 && (
            <section className="space-y-2">
              <Caption>Инструменты</Caption>
              <div className="flex flex-wrap gap-2">{t.tools_used.map((tool) => <ToolChip key={tool.id} tool={tool} />)}</div>
            </section>
          )}
          {confirmDelete && <p className="text-meta text-p-text-muted">Нажми ещё раз — задача уйдёт из списков вместе с обсуждением.</p>}
          <Button variant="destructive" block loading={remove.isPending} onClick={() => (confirmDelete ? remove.mutate() : setConfirmDelete(true))}>
            {confirmDelete ? "Точно удалить задачу" : "Удалить задачу"}
          </Button>
        </div>
      </Sheet>

      {closingStatus && <CloseTaskSheet taskId={t.id} version={t.version} statusId={closingStatus}
        completionNote={t.completion_note_md} onClose={() => setClosingStatus(null)} />}
      <ConflictSheet conflict={update.conflict} attempted={update.attempted}
        onReload={() => update.reload()} onCancel={update.dismiss} />
    </div>
  );
}
